import React, { useEffect } from 'react'
import Header, { HeaderVariant } from '../Elements/Header'
import CardFormHeader from '../Card/CardFormHeader'
import { useTranslation } from 'react-i18next'
import { useShowOnDesktop } from '../../hooks/useShowOnDesktop'
import { FormDivider } from '../Elements/FormDivider'
import { Box, TextField } from '@mui/material'
import CheckboxControl from '../Checkboxes/CheckboxControl'
import { useTranscriptContext } from '../Context/TranscriptContext'
import { useAccountContext } from '../Context/AccountContext'
import DropDown, { DropDownVariant } from '../Menus/DropDown'
import { useTheme } from '@mui/material/styles'
import studentKeyToStudentIdForTranscript from '../../utils/studentKeyToStudentIdForTranscript'
import DateField, {
  DateFieldVariant,
  dateFormatMMDDYYY,
} from '../Elements/DateField'
import { dateToDashStringReinterpretedAsLocal } from '../../utils/dateUtility'
import { parse } from 'date-fns'

export interface StudentInformationHeaderProps {
  isFieldDisabled?: boolean
  isSameAddress?: boolean
  handleSameAddress: (value: boolean) => void
}

export const StudentInformationHeader: React.FC<
  StudentInformationHeaderProps
> = ({ isFieldDisabled, isSameAddress, handleSameAddress }) => {
  const { t } = useTranslation()
  const showOnDesktop = useShowOnDesktop()
  const {
    updateTranscriptDetails,
    transcriptDetails,
    transcriptValidity,
    updateFormValidity,
  } = useTranscriptContext()
  const { students } = useAccountContext()
  const theme = useTheme()

  const selectedStudentKey =
    transcriptDetails.student?.studentKey ?? transcriptDetails.studentKey

  const selectedStudent =
    transcriptDetails.student?.studentKey &&
    transcriptDetails.student?.firstName
      ? transcriptDetails.student
      : students.find((student) => student.studentKey === selectedStudentKey)

  useEffect(() => {
    if (!selectedStudentKey || selectedStudentKey <= 0) return
    if (!selectedStudent) return

    if (transcriptDetails.student?.studentKey === selectedStudent.studentKey)
      return

    updateTranscriptDetails({
      ...transcriptDetails,
      studentKey: selectedStudent.studentKey,
      lastName: selectedStudent.lastName,
      student: {
        studentKey: selectedStudent.studentKey,
        firstName: selectedStudent.firstName,
        lastName: selectedStudent.lastName,
      },
    })
  }, [
    selectedStudentKey,
    selectedStudent,
    transcriptDetails,
    updateTranscriptDetails,
  ])

  const textFieldOnChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target
    const emailInputName = 'studentEmail'
    const isEmailField = name === emailInputName

    // Database limits student email field to 320 characters
    if (name === emailInputName && value.length > 320) {
      return
    }

    updateFormValidity({
      ...transcriptValidity,
      [name]: {
        input: true,
        ...(isEmailField && { email: true }),
      },
    })

    updateTranscriptDetails({
      ...transcriptDetails,
      [name]: value,
    })
  }

  const dateFieldOnChange = (
    _: React.ChangeEvent<HTMLInputElement>,
    value?: string
  ) => {
    /*
     * Datefield should be disabled if there is
     * no studentKey, this should never be true. Thank you typescript XD.
     */
    if (!transcriptDetails.student?.studentKey) return

    updateTranscriptDetails({
      ...transcriptDetails,
      studentDateOfBirth: value
        ? parse(value, dateFormatMMDDYYY, new Date())
        : undefined,
    })

    updateFormValidity({
      ...transcriptValidity,
      dateOfBirth: {
        input: true,
      },
    })
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault()
  }

  return (
    <section aria-labelledby="student-information-header">
      <CardFormHeader
        header={
          <Header
            id="student-information-header"
            component={'h2'}
            variant={HeaderVariant.Card}
            headerName={t(
              'Transcripts.StudentInformationHeader.Header.StudentInformation',
              'Student Information'
            )}
          />
        }
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(1.25),
        }}
      >
        <Box
          marginLeft={theme.spacing(2)}
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              sm: '1fr',
              md: '1fr 1fr',
            },
          }}
          gap={theme.spacing(2.5)}
        >
          <DropDown
            formControlProps={{
              marginBottom: showOnDesktop ? '' : theme.spacing(0),
              transform: 'translateY(-2px)',
            }}
            id="studentName"
            menuOptions={students.map((student) => ({
              name: `${student.firstName} ${student.lastName}`.trim(),
              id: student.studentKey,
            }))}
            required
            helperText={
              !transcriptValidity.studentKey.input
                ? t(
                    'Transcripts.StudentInformationHeader.Error.StudentKey',
                    'Student is required.'
                  )
                : undefined
            }
            error={!transcriptValidity.studentKey.input}
            handleSelection={(_, __, selectionId) => {
              const selected = students.find(
                (student) => student.studentKey === Number(selectionId)
              )
              if (!selected) return

              updateFormValidity({
                ...transcriptValidity,
                studentKey: { input: true },
              })

              updateTranscriptDetails({
                ...transcriptDetails,
                studentKey: selected.studentKey,
                lastName: selected.lastName,
                student: {
                  studentKey: selected.studentKey,
                  firstName: selected.firstName,
                  lastName: selected.lastName,
                },
              })
            }}
            disabled={isFieldDisabled}
            label={t(
              'Transcripts.StudentInformationHeader.Field.StudentName',
              'Student Full Name'
            )}
            variant={DropDownVariant.FormField}
            fullWidth
            /**
             * It will always be as a `Transcript` even though we're not
             * providing the name to the createTranscript call. We can utilize
             * the display name & key on selection.
             */
            value={
              selectedStudent
                ? `${selectedStudent.firstName} ${
                    selectedStudent.lastName ?? ''
                  }`.trim()
                : ''
            }
            cssProps={{
              padding: 0,
              height: 'auto',
              width: '100%',
              textAlign: 'left',
            }}
          />

          <TextField
            id="studentID"
            name="studentID"
            label={t(
              'Transcripts.StudentInformationHeader.Field.StudentID',
              'Student ID'
            )}
            variant="filled"
            sx={{
              width: '100%',
            }}
            value={
              selectedStudentKey && selectedStudentKey > 0
                ? studentKeyToStudentIdForTranscript(selectedStudentKey)
                : ''
            }
            fullWidth
            disabled
          />

          <DateField
            id="dateOfBirthEntry"
            inputProps={{
              'aria-label': t(
                'Transcripts.StudentInformationHeader.Field.DateOfBirth',
                'Date of Birth Entry'
              ),
            }}
            disabled={isFieldDisabled || !transcriptDetails.student?.studentKey}
            variant="filled"
            type={isFieldDisabled ? 'text' : 'date'}
            InputLabelProps={{ shrink: true }}
            fullWidth
            value={
              !!transcriptDetails.studentDateOfBirth
                ? dateToDashStringReinterpretedAsLocal(
                    transcriptDetails.studentDateOfBirth
                  )
                : ''
            }
            name="dateOfBirthEntry"
            onChangeForParent={dateFieldOnChange}
            helperTextForParent={t(
              'Transcripts.StudentInformationHeader.Error.DateOfBirthRequired',
              'Student Date of Birth Is Required.'
            )}
            error={!transcriptValidity.dateOfBirth.input}
            dateVariant={DateFieldVariant.DateOfBirth}
            onKeyDown={onKeyDown}
          />
          <TextField
            id="studentEmail"
            name="studentEmail"
            label={t(
              'Transcripts.StudentInformationHeader.Field.Email',
              'Email'
            )}
            variant="filled"
            disabled={isFieldDisabled}
            sx={{
              width: '100%',
            }}
            value={transcriptDetails.studentEmail ?? ''}
            onChange={textFieldOnChange}
            helperText={
              !transcriptValidity.studentEmail.email
                ? t(
                    'Transcripts.StudentInformationHeader.Error.StudentEmailNotValid',
                    'Student Email must be a valid email.'
                  )
                : !transcriptValidity.studentEmail.input
                ? t(
                    'Transcripts.StudentInformationHeader.Error.StudentEmailRequired',
                    'Student Email Is Required.'
                  )
                : null
            }
            FormHelperTextProps={{
              disabled: false,
            }}
            fullWidth
            error={
              !transcriptValidity.studentEmail.input ||
              !transcriptValidity.studentEmail.email
            }
          />
        </Box>
      </Box>
      <FormDivider />
      <CheckboxControl
        disabled={isFieldDisabled}
        label={t(
          'Transcripts.StudentInformationHeader.Checkbox.SameAs',
          'Student Address same as School Address'
        )}
        checkboxValue={!!isSameAddress}
        OnChange={(checked) => handleSameAddress(checked)}
        name={'same-address-as-school'}
      />
    </section>
  )
}

export default StudentInformationHeader
